import type { Language } from '../contexts/TranslationContext';
import { ApiError } from '../types/errors';
import { apiService } from './api';

export type GuestSignupReason = 'daily-budget' | 'period-ip' | 'account-login' | 'registration';

export interface PublicGuestQuestionRequest {
  question: string;
  locale: Language;
  captchaToken: string;
  website: string;
  recoveryToken?: string;
}

const SIGNUP_REASON_BY_CODE: Record<string, GuestSignupReason> = {
  GUEST_DAILY_BUDGET_EXHAUSTED: 'daily-budget',
  GUEST_IP_PERIOD_LIMIT: 'period-ip',
  GUEST_IP_ACCOUNT_REQUIRED: 'account-login',
  GUEST_REGISTRATION_REQUIRED: 'registration',
};

export function getGuestSignupReason(error: unknown): GuestSignupReason | null {
  if (!(error instanceof ApiError) || error.statusCode !== 403
    || typeof error.details !== 'object' || error.details === null) return null;
  const code = (error.details as Record<string, unknown>).code;
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(SIGNUP_REASON_BY_CODE, code)
    ? SIGNUP_REASON_BY_CODE[code] : null;
}

export interface GuestAnswerReceipt { token: string; expiresAt: string; preview?: string }
export interface GuestAnswerClaimRequest {
  receiptToken: string;
  email: string;
  locale: Language;
  captchaToken: string;
  website: string;
}

export async function askPublicGuestQuestion(request: PublicGuestQuestionRequest): Promise<GuestAnswerReceipt> {
  let receipt: GuestAnswerReceipt | null = null;
  await apiService.streamPost('/public/ask', request, (event) => {
    if (event.type !== 'done' || typeof event.receipt !== 'object' || event.receipt === null) return;
    const candidate = event.receipt as Record<string, unknown>;
    if (typeof candidate.token === 'string' && candidate.token.length > 0
      && typeof candidate.expiresAt === 'string' && Number.isFinite(Date.parse(candidate.expiresAt))) {
      const preview = typeof candidate.preview === 'string' ? candidate.preview.trim() : '';
      receipt = {
        token: candidate.token, expiresAt: candidate.expiresAt,
        ...(preview && preview.length <= 360 && !/[<>]|\p{Cc}/u.test(preview) ? { preview } : {}),
      };
    }
  }, { skipUserAuth: true, inactivityTimeoutMs: 60_000 });
  if (!receipt) throw new Error('GUEST_ANSWER_INCOMPLETE');
  return receipt;
}

export async function claimGuestAnswer(request: GuestAnswerClaimRequest): Promise<void> {
  await apiService.post('/public/ask/claim', request, undefined, true, false);
}
